import { useEffect, useState } from 'react';

import { Box, type SxProps, type Theme } from '@mui/material';

import { AdvancedFilters } from '@modules/_shared/components/filters/advanced-filters/AdvancedFilters';
import {
  QuickFilter,
  type QuickFiltersProps,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { SchemaProvider } from '@modules/_shared/components/filters/SchemaProvider';
import { parseWhereParam } from '@modules/_shared/utils/listUrlParams';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';

export type AddWhereClause = (
  where: any,
  schemaPath: string[],
  value: string | string[] | number | boolean | Date | object,
) => any;

const inOperators = ['_in', '_nin'];
const xorOperators = ['_and', '_or'];
const arrayOperators = [...inOperators, ...xorOperators];

export const addWhereClause: AddWhereClause = (where, schemaPath, value) => {
  const [, ...path] = schemaPath;
  const newWhere = JSON.parse(JSON.stringify(where));
  // Initialize the current position to start at the new object
  let current = newWhere;
  // Iterate over the path array, stopping before the last element
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    // If the key doesn't exist at the current path, initialize it as an empty object
    if (!current[key]) {
      if (arrayOperators.includes(key)) {
        current[key] = [];
      } else {
        current[key] = {};
      }
    }
    current = current[key];
  }

  // Set the value at the final key in the path
  const lastKey = path[path.length - 1];
  const penultimateKey = path[path.length - 2];

  // The value at the path is not yet an array, so we can just set the value
  if (arrayOperators.includes(lastKey) && !Array.isArray(current[lastKey])) {
    current[lastKey] = Array.isArray(value) ? value : [value];
  } else if (xorOperators.includes(penultimateKey)) {
    current.push({ [lastKey]: value });
  } else if (inOperators.includes(lastKey)) {
    if (!Array.isArray(value)) {
      current[lastKey].push(value);
    } else {
      current[lastKey] = [...value, ...current[lastKey]];
    }
  } else {
    current[lastKey] = value;
  }

  // Return the updated object, leaving the original `where` object unchanged
  return newWhere;
};

export type DeleteWhereClause = (
  where: any,
  schemaPath: string[],
  deleteEmptyParents?: Boolean,
) => any;

export const deleteWhereClause: DeleteWhereClause = (
  where,
  schemaPath,
  deleteEmptyParents = false,
) => {
  const [, ...path] = schemaPath;
  const newWhere = JSON.parse(JSON.stringify(where));
  let current = newWhere;
  const stack = []; // To keep track of the path if we need to delete empty parents
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (!current[key]) {
      return newWhere;
    }
    stack.push(current);
    current = current[key];
  }
  // If the parent is an object, delete the key
  // If the parent is an array, remove the item
  const lastKey = path[path.length - 1];
  if (Array.isArray(current)) {
    current.splice(parseInt(lastKey), 1);
  } else {
    delete current[lastKey];
  }

  if (deleteEmptyParents) {
    // If the parent object is now empty, delete it as well
    for (let i = path.length - 2; i >= 0; i--) {
      const key = path[i];
      const parent = stack.pop();
      if (Object.keys(parent[key]).length === 0) {
        delete parent[key];
      } else {
        break;
      }
    }
  }
  return newWhere;
};

export type QuickFilterDefinition = {
  path: string[];
  label: string;
  shouldDisplay?: (where: {}) => boolean;
  disabled?: (where: {}) => boolean;
  onChange?: (value: any, whereClause: {}) => void | {};
  displayedColumn?: string;
} & Pick<
  QuickFiltersProps,
  | 'filter'
  | 'filterAutocompleteRenderOption'
  | 'isNullable'
  | 'includeParentsPlaces'
  | 'expandSelectionAncestors'
  | 'orderBy'
  | 'excludeMode'
  | 'enumValueLabel'
  | 'nullCheckMode'
  | 'dateFields'
>;

export interface FilterProps {
  tableName?: string;
  // See AdvancedFilters: the `crm_entity` saved views are filed under.
  smartListEntity?: string;
  quickFilters?: QuickFilterDefinition[];
  expandedFilterType?: 'advanced' | 'quick' | null;
  queryParamsScope?: string;
  isScraperSchema?: boolean;
}

export const scrollablePanelSx: SxProps<Theme> = {
  maxHeight: '50vh',
  overflowY: 'auto',
};

const filterPanelSx: SxProps<Theme> = {
  ...scrollablePanelSx,
  borderTop: '1px solid',
  borderColor: 'divider',
};

export const Filters = ({
  tableName,
  smartListEntity,
  quickFilters,
  expandedFilterType,
  queryParamsScope,
  isScraperSchema,
}: FilterProps) => {
  const [searchParams] = useScopedSearchParams(queryParamsScope);
  const whereParam = searchParams.get('where');
  // Same guard as the toolbar (`parseWhereParam`): a malformed or non-object
  // `?where` reads as no filter everywhere, never as a different filter in
  // each consumer.
  const [where, setWhere] = useState(() => parseWhereParam(whereParam));

  // useEffect(() => {
  //   setSearchParams({ where: JSON.stringify(where) });
  // }, [where, setSearchParams]);

  useEffect(() => {
    setWhere(parseWhereParam(whereParam));
  }, [whereParam]);

  return (
    <SchemaProvider isScraperSchema={isScraperSchema}>
      {expandedFilterType === 'quick' && (quickFilters?.length ?? 0) > 0 && (
        <Box
          sx={{
            ...filterPanelSx,
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            padding: 1,
          }}
        >
          {quickFilters?.map(qf => {
            if (qf.shouldDisplay != null && !qf.shouldDisplay(where)) {
              return null;
            }

            return (
              <div key={qf.path.join('.')}>
                <QuickFilter
                  addWhereClause={addWhereClause}
                  deleteWhereClause={deleteWhereClause}
                  queryParamsScope={queryParamsScope}
                  isScraperSchema={isScraperSchema}
                  {...qf}
                />
              </div>
            );
          })}
        </Box>
      )}

      {expandedFilterType === 'advanced' && tableName && (
        <Box sx={filterPanelSx}>
          <AdvancedFilters
            where={where}
            setWhere={setWhere}
            tableName={tableName}
            smartListEntity={smartListEntity}
            queryParamsScope={queryParamsScope}
          />
        </Box>
      )}
    </SchemaProvider>
  );
};
